import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import NotificationBell from "@/components/NotificationBell";
import {
  Gauge, Calendar, LayoutDashboard, CreditCard, Shield,
  LogOut, User, Menu, X, Users, Zap,
} from "lucide-react";

const LOGO_WHITE = "https://customer-assets-jai6qajn.emergentagent.net/job_type-race-4/artifacts/a9pwlfiw_white_text_racing.png";
const LOGO_DARK = "https://customer-assets-jai6qajn.emergentagent.net/job_type-race-4/artifacts/ne0g788w_Text-RAcing-04-02-e1599230884415.png";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const avatarSrc = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : null);

const NavAvatar = ({ user, size = 36 }) => (
  <div className="rounded-full overflow-hidden bg-race-red text-race-bg flex items-center justify-center font-heading shrink-0"
       style={{ width: size, height: size, fontSize: size * 0.45 }} data-testid="nav-avatar">
    {user.profile_image_url
      ? <img src={avatarSrc(user.profile_image_url)} alt={user.name} className="w-full h-full object-cover" />
      : <span>{(user.name || "R").slice(0, 1).toUpperCase()}</span>}
  </div>
);

function useThemedLogo() {
  const { settings } = useSettings();
  const isLight = settings.theme === "light"
    || (settings.theme === "system" && typeof window !== "undefined"
        && window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches);
  return isLight ? LOGO_DARK : LOGO_WHITE;
}

export default function Nav() {
  const { user, logout } = useAuth();
  const logo = useThemedLogo();
  const navigate = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  // Close the menu whenever the route changes.
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const items = [];
  if (user && user.role) {
    items.push({ to: "/dashboard", label: "Dashboard", Icon: LayoutDashboard, testid: "nav-dashboard" });
  }
  items.push({ to: "/practice", label: "Practice", Icon: Gauge, testid: "nav-practice" });
  if (user && user.role) {
    items.push({ to: "/calendar", label: "Calendar", Icon: Calendar, testid: "nav-calendar" });
    items.push({ to: "/license", label: "BR License", Icon: CreditCard, testid: "nav-license" });
    items.push({ to: "/buy-credits", label: "Buy Credits", Icon: Zap, testid: "nav-buy-credits" });
    items.push({ to: "/profile", label: "Profile", Icon: User, testid: "nav-profile" });
    items.push({ to: "/racing-team", label: "Racing Team", Icon: Users, testid: "nav-racing-team" });
    if (user.role === "admin") items.push({ to: "/admin", label: "IBRA Admin", Icon: Shield, testid: "nav-admin" });
  }

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  return (
    <header className="app-nav sticky top-0 z-50 bg-race-surface/90 backdrop-blur-md border-b border-race-line">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center shrink-0" data-testid="brand-logo">
          <img src={logo} alt="Text Racing" className="h-7 sm:h-8 w-auto" />
        </Link>

        <div className="flex items-center gap-3 xl:gap-5">
          {/* Desktop inline nav */}
          <nav className="hidden xl:flex items-center gap-1.5" data-testid="desktop-nav">
            {items.map(({ to, label, Icon, testid }) => {
              const active = loc.pathname === to;
              return (
                <Link key={to} to={to} data-testid={testid}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-md font-mono text-xs uppercase tracking-wide whitespace-nowrap transition-colors ${
                    active ? "text-race-red bg-race-elevated" : "text-race-txt hover:text-race-red hover:bg-race-elevated"
                  }`}>
                  <Icon size={15} className="shrink-0" />
                  {label}
                </Link>
              );
            })}
          </nav>

          {user && user.role && (
            <Link to="/profile" data-testid="nav-profile-link"
              className="hidden sm:flex items-center gap-2.5 xl:border-l xl:border-race-line xl:pl-5 group">
              <div className="flex flex-col items-end leading-tight">
                <span className="font-mono text-sm text-race-txt whitespace-nowrap group-hover:text-race-red transition-colors">{user.name}</span>
                <span className="overline text-race-sub whitespace-nowrap">{Math.round(user.avg_cps || 0)} CPS AVG</span>
              </div>
              <NavAvatar user={user} size={36} />
            </Link>
          )}

          {/* Desktop log out / sign in */}
          <div className="hidden xl:block">
            {user && user.role ? (
              <button onClick={handleLogout} data-testid="logout-btn-desktop" aria-label="Log out"
                className="flex items-center justify-center w-10 h-10 rounded-md border border-race-line text-race-sub hover:border-race-red hover:text-race-red transition-colors">
                <LogOut size={18} />
              </button>
            ) : (
              <Link to="/login" data-testid="nav-signin-desktop"
                className="flex items-center gap-2 px-4 py-2 rounded-md bg-race-red text-race-bg font-mono text-xs uppercase tracking-wide hover:bg-race-redhover transition-colors">
                <User size={16} /> Sign In
              </Link>
            )}
          </div>

          {user && user.role && <NotificationBell />}

          {/* Mobile hamburger */}
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-menu-toggle"
            aria-label="Menu"
            aria-expanded={open}
            className="xl:hidden flex items-center justify-center w-11 h-11 -mr-1 rounded-md border border-race-line text-race-txt hover:border-race-red hover:text-race-red transition-colors">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Dropdown menu */}
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} data-testid="nav-backdrop" />
          <nav
            data-testid="nav-menu-panel"
            className="absolute right-4 top-[68px] z-50 w-64 bg-race-surface border border-race-line rounded-md shadow-2xl p-2 animate-slide-up">
            {user && user.role && (
              <div className="sm:hidden flex items-center gap-3 px-3 py-3 border-b border-race-line mb-1">
                <NavAvatar user={user} size={40} />
                <div>
                  <div className="font-mono text-sm text-race-txt">{user.name}</div>
                  <div className="overline text-race-sub">{Math.round(user.avg_cps || 0)} CPS AVG</div>
                </div>
              </div>
            )}

            {items.map(({ to, label, Icon, testid }) => {
              const active = loc.pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={`flex items-center gap-3 px-3 py-3 rounded-md transition-colors ${
                    active ? "bg-race-elevated text-race-red" : "text-race-txt hover:bg-race-elevated"
                  }`}>
                  <Icon size={22} />
                  <span className="font-mono text-sm uppercase tracking-wide">{label}</span>
                </Link>
              );
            })}

            <div className="border-t border-race-line mt-1 pt-1">
              {user && user.role ? (
                <button
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-md text-race-sub hover:bg-race-elevated hover:text-race-txt transition-colors">
                  <LogOut size={22} />
                  <span className="font-mono text-sm uppercase tracking-wide">Log Out</span>
                </button>
              ) : (
                <Link
                  to="/login"
                  data-testid="nav-signin"
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-md bg-race-red text-race-bg hover:bg-race-redhover transition-colors">
                  <User size={22} />
                  <span className="font-mono text-sm uppercase tracking-wide">Sign In</span>
                </Link>
              )}
            </div>
          </nav>
        </>
      )}
    </header>
  );
}
